/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  useEffect,
  useMemo
} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {MessageEndpoint, MessageEndpointTypes} from '@ion/core/src/types/entities';
import FormItem from '@ion/dls/src/components/forms/FormItem';
import FormSecureTextField from '@ion/dls/src/components/forms/FormSecureTextField';
import FormTextField from '@ion/dls/src/components/forms/FormTextField';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {
  getInitialKafkaFormState,
  getIsKafkaPlainOrScramType,
  getIsKafkaType,
  MessageEndpointFormFields
} from './MessageEndpointUtils';

export interface KafkaMessageEndpointProps {
  messageEndpoint?: MessageEndpoint;
  formState: FormState;
  setFormState: Function;
  type: MessageEndpointTypes;
  isView?: boolean;
  isAdd?: boolean;
  isLoggedinUserAccount?: boolean;
  onFieldChange: (e: ChangeEvent) => void;
}

/**
 * Displays a Kafka style account message endpoint form & handles all related form functionality.
 *
 * This is strictly to be used in the context of a @see MessageEndpoint component.
 *
 * ```typescript
 *
 * import KafkaMessageEndpoint from './KafkaMessageEndpoint';
 *
 * <KafkaMessageEndpoint isView={isView}
 *   isLoggedinUserAccount={isLoggedinUserAccount}
 *   formState={formState}
 *   setFormState={setFormState}
 *   messageEndpoint={messageEndpoint}
 *   type={messageType}
 *   onFieldChange={onFieldChange} />
 *
 * ```
 */
export const KafkaMessageEndpoint = forwardRef(({
  messageEndpoint,
  type,
  isView,
  isAdd,
  isLoggedinUserAccount,
  formState,
  setFormState,
  onFieldChange
}: KafkaMessageEndpointProps, ref) => {
  const kafkaPlainOrScram = getIsKafkaPlainOrScramType(type);

  useEffect(function(): void {
    if (getIsKafkaType(type)) {
      setFormState(getInitialKafkaFormState(type, messageEndpoint?.configuration));
    } else {
      setFormState(null, true);
    }
  }, [messageEndpoint, type]);

  const {
    bootstrapServers,
    topicName,
    username,
    password
  } = translate('admin-groups:dialog.messageEndpoint.fields', {returnObjects: true});

  return (
    <>
      <FormItem>
        <FormTextField required
          fieldLabel={bootstrapServers.label}
          placeholder={bootstrapServers.placeholder}
          errorText={bootstrapServers.errorMessage}
          hasError={formState[MessageEndpointFormFields.BOOTSTRAP_SERVERS]?.error}
          value={formState[MessageEndpointFormFields.BOOTSTRAP_SERVERS]?.value}
          name={MessageEndpointFormFields.BOOTSTRAP_SERVERS}
          onChange={onFieldChange}
          disabled={isView || isLoggedinUserAccount} />
      </FormItem>
      <FormItem>
        <FormTextField required
          fieldLabel={topicName.label}
          placeholder={topicName.placeholder}
          errorText={topicName.errorMessage}
          hasError={formState[MessageEndpointFormFields.KAFKA_TOPIC_NAME]?.error}
          value={formState[MessageEndpointFormFields.KAFKA_TOPIC_NAME]?.value}
          name={MessageEndpointFormFields.KAFKA_TOPIC_NAME}
          onChange={onFieldChange}
          disabled={isView || isLoggedinUserAccount} />
      </FormItem>
      {
        kafkaPlainOrScram && (
          <>
            <FormItem>
              <FormTextField required
                fieldLabel={username.label}
                placeholder={username.placeholder}
                errorText={username.errorMessage}
                hasError={formState[MessageEndpointFormFields.USERNAME]?.error}
                value={formState[MessageEndpointFormFields.USERNAME]?.value}
                name={MessageEndpointFormFields.USERNAME}
                onChange={onFieldChange}
                disabled={isView || isLoggedinUserAccount} />
            </FormItem>
            {!isView && (
              <FormItem>
                <FormSecureTextField required={isAdd || isView}
                  fallbackValue={!isAdd ? undefined : ''}
                  fieldLabel={password.label}
                  placeholder={password.placeholder}
                  errorText={password.errorMessage}
                  hasError={formState[MessageEndpointFormFields.PASSWORD]?.error}
                  value={formState[MessageEndpointFormFields.PASSWORD]?.value}
                  name={MessageEndpointFormFields.PASSWORD}
                  onChange={onFieldChange}
                  disabled={isView || isLoggedinUserAccount} />
              </FormItem>
            )}
          </>
        )
      }
    </>
  );
});

export default KafkaMessageEndpoint;
